import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { adminApi, type SyncJobRun } from '@/api';

export function SyncJobDetail() {
  const { id } = useParams<'id'>();
  const [run, setRun] = useState<SyncJobRun | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    adminApi.syncJobs
      .getRun(id)
      .then(setRun)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="text-slate-500">Loading…</div>;
  if (error) return <div className="text-red-600">{error}</div>;
  if (!run) return <div className="text-slate-500">Run not found</div>;

  const errors = run.errors ?? [];

  return (
    <div>
      <div className="flex items-center gap-4 mb-6 flex-wrap">
        <Link to="/syncs/jobs" className="text-slate-600 hover:text-slate-900 text-sm">← Sync Jobs</Link>
        <h1 className="text-2xl font-semibold text-slate-800">Sync Job Run</h1>
      </div>
      <div className="space-y-4">
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div>
              <dt className="text-sm text-slate-500">Started</dt>
              <dd className="font-medium">{new Date(run.startedAt).toLocaleString()}</dd>
            </div>
            <div>
              <dt className="text-sm text-slate-500">Finished</dt>
              <dd className="font-medium">
                {run.finishedAt ? new Date(run.finishedAt).toLocaleString() : '-'}
              </dd>
            </div>
            <div>
              <dt className="text-sm text-slate-500">Status</dt>
              <dd>
                <span
                  className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                    run.status === 'completed'
                      ? 'bg-green-100 text-green-800'
                      : run.status === 'failed'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-amber-100 text-amber-800'
                  }`}
                >
                  {run.status}
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-sm text-slate-500">Processed / Skipped</dt>
              <dd className="font-medium">{run.processed} / {run.skipped}</dd>
            </div>
          </dl>
        </div>
        {errors.length > 0 && (
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <h2 className="text-lg font-medium text-slate-800 mb-2">Errors</h2>
            <ul className="space-y-2 text-sm">
              {errors.map((e, i) => (
                <li key={i} className="flex flex-col gap-0.5">
                  <a
                    href={e.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-indigo-600 hover:underline truncate max-w-2xl"
                  >
                    {e.url}
                  </a>
                  <span className="text-red-600">{e.message}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {run.details && Object.keys(run.details).length > 0 && (
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <h2 className="text-lg font-medium text-slate-800 mb-2">Details</h2>
            <pre className="text-sm text-slate-600 overflow-auto">
              {JSON.stringify(run.details, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}
